import type {
  IThreatMatchTableRow,
  TDefinesData,
  TFindingsForMatches,
  TThreatMatchNode,
} from "./types";

import type {
  GetFaDefinesVulnsQuery,
  GetGroupMatchesFindingsQuery,
  GetGroupThreatMatchesQuery,
} from "gql/graphql";

const isWithinMatches = (
  findingTitle: string,
  matchNumbers: number[],
): boolean => {
  return matchNumbers.some((matchNumber): boolean =>
    findingTitle.startsWith(matchNumber.toString().padStart(3, "0")),
  );
};

const getDefinesTypologyTitle = (
  vulnTypologyId: number,
  definesData: GetFaDefinesVulnsQuery,
): string => {
  const match = definesData.criteriaConnection.edges
    .map((edge): TDefinesData => edge.node)
    .find(
      (edge): boolean =>
        edge.findingNumber === vulnTypologyId.toString().padStart(3, "0"),
    );

  return match?.en.title ?? "";
};

const parseMatchesData = (
  matchesData: GetGroupThreatMatchesQuery,
  findingsData: GetGroupMatchesFindingsQuery,
  definesData: GetFaDefinesVulnsQuery,
): IThreatMatchTableRow[] => {
  const filteredMatchesEdges = matchesData.group.threatMatches.edges
    .map((edge): TThreatMatchNode | null => edge?.node ?? null)
    .filter((node): node is TThreatMatchNode => node !== null);
  const matchNumbers = filteredMatchesEdges.map(
    (edge): number => edge.vulnType,
  );

  const filteredFindings =
    findingsData.group.findings?.filter(
      (finding): finding is NonNullable<TFindingsForMatches>[0] =>
        finding !== null && isWithinMatches(finding.title, matchNumbers),
    ) ?? [];

  return filteredMatchesEdges
    .map((edge): IThreatMatchTableRow => {
      const finding = filteredFindings.find(
        (fin): boolean =>
          fin?.title.startsWith(edge.vulnType.toString().padStart(3, "0")) ??
          false,
      );

      return {
        ...edge,
        closedVulns: finding?.vulnerabilitiesSummaryV4.closed ?? 0,
        findingId: finding?.id,
        openVulns: finding?.vulnerabilitiesSummaryV4.open ?? 0,
        severityScoreV4: finding?.severityScoreV4 ?? 0,
        title:
          edge.title ??
          finding?.title ??
          getDefinesTypologyTitle(edge.vulnType, definesData),
      };
    })
    .sort(
      (itemA, itemB): number =>
        itemB.severityScoreV4 - itemA.severityScoreV4 ||
        itemB.vulnType - itemA.vulnType,
    );
};

export { parseMatchesData, getDefinesTypologyTitle };
